/* eslint-disable no-restricted-imports */
'use client'

import { useAppKitAccount, useAppKitNetwork, useDisconnect } from '@reown/appkit/react'
import { SCREEN_ADDRESS_ROUTE } from '@repo/address-screening/client'
import { useIsMounted, useMandatoryContext } from '@repo/hooks'
import { createContext, type PropsWithChildren, useEffect, useRef, useState } from 'react'
import { type Address, isAddressEqual } from 'viem'
import { useAccount, useConnections, useDisconnect as useWagmiDisconnect, useEnsName } from 'wagmi'

import { isE2E_Next } from '../configs/process'
import { useAuthorizedUserQuery } from '../hooks/useAuthorizedUserQuery'
import { getChain } from '../utils/chain.utils'
import { ChainId } from '../web3.types'

type UseUserAccountResponse = ReturnType<typeof useUserAccountHook>
const UserAccountContext = createContext<UseUserAccountResponse>({} as UseUserAccountResponse)

function useUserAccountHook(screenAddressEndpoint: string, disableScreening: boolean) {
  const isMounted = useIsMounted()

  const accountQuery = useAccount()
  const { isConnected, address } = useAppKitAccount()
  const { chainId, switchNetwork } = useAppKitNetwork()
  const { disconnect: disconnectAppKit } = useDisconnect()
  const { disconnectAsync: disconnectWagmi } = useWagmiDisconnect()
  const [screeningBlocked, setScreeningBlocked] = useState(false)
  const [screeningBlockedCount, setScreeningBlockedCount] = useState(0)
  const disconnectedAddressRef = useRef<Address | undefined>(undefined)
  // oxlint-disable-next-line typescript/no-unused-vars
  const { address: donotUseThisAddress, ...queryWithoutAddress } = accountQuery

  // Prefer AppKit's address, but fall back to wagmi's address during reconnection
  // (wagmi has the address from cookies via cookieToInitialState)
  const userAddress = (address as Address | undefined) ?? accountQuery.address

  const { data: ensName } = useEnsName({ address: userAddress, chainId: 1 })

  const chain = chainId && chainId in ChainId ? getChain(chainId as ChainId) : undefined

  // The consumer's route is same-origin, so screening runs in every environment.
  // Mocked environments are the exception: they have no route (nor Chainalysis
  // key), so a fail-closed screen would disconnect their wallet.
  const screeningEnabled = !isE2E_Next && !disableScreening
  const { data: isAuthorized } = useAuthorizedUserQuery(userAddress, {
    enabled: screeningEnabled,
    screenAddressEndpoint
  })

  // The effect's state lands a commit after the verdict, so mask on the verdict itself too.
  const isScreenedOut = screeningBlocked || (screeningEnabled && isAuthorized === false)

  // Enforce on wagmi, not AppKit: AppKit's `disconnect()` is a no-op until its `caipAddress`
  // syncs, which lands after wagmi connects (pre-approved reconnects stayed signable). Wait for
  // `status === 'connected'` (cookie hydration reports the address while `reconnecting`) and
  // drop every connection, since wagmi promotes the next one when only the current is dropped.
  const isWagmiConnected = accountQuery.status === 'connected'
  const wagmiAddress = accountQuery.address
  const connections = useConnections()
  // Ref, not deps: AppKit's `disconnect` is a new function every render.
  const teardownRef = useRef({ connections, disconnectWagmi, disconnectAppKit, userAddress })
  teardownRef.current = { connections, disconnectWagmi, disconnectAppKit, userAddress }
  useEffect(() => {
    // Leaves `screeningBlocked` latched so the toast survives the disconnect.
    if (!userAddress || !isWagmiConnected) {
      disconnectedAddressRef.current = undefined
      return
    }

    if (!screeningEnabled || isAuthorized) {
      disconnectedAddressRef.current = undefined
      setScreeningBlocked(false)
      return
    }

    // Only tear down the session we screened: AppKit's address can lag wagmi's, so a stale
    // blocked verdict must not disconnect a wallet that connected in the meantime.
    if (wagmiAddress && !isAddressEqual(wagmiAddress, userAddress)) return

    if (
      !disconnectedAddressRef.current ||
      !isAddressEqual(disconnectedAddressRef.current, userAddress)
    ) {
      disconnectedAddressRef.current = userAddress
      setScreeningBlocked(true)
      setScreeningBlockedCount(count => count + 1)
      console.warn('User is not authorized, disconnecting')
      const { connections, disconnectWagmi, disconnectAppKit } = teardownRef.current
      Promise.allSettled(connections.map(({ connector }) => disconnectWagmi({ connector })))
        .then(results => {
          const failed = results.find(result => result.status === 'rejected')
          if (failed) throw failed.reason
        })
        .finally(() => {
          // Skip if another wallet took over meanwhile.
          const latest = teardownRef.current.userAddress
          if (latest === undefined || isAddressEqual(latest, userAddress)) return disconnectAppKit()
        })
        .catch((error: unknown) => {
          // The connection survived; the next status change retries, and the account below
          // already reads as disconnected.
          console.warn('Screening disconnect failed', error)
        })
    }
  }, [screeningEnabled, userAddress, wagmiAddress, isWagmiConnected, isAuthorized])

  // TODO: Check if this is needed with AppKit
  // useSafeAppConnectionGuard(accountQuery.connector, chainId)

  // The usage of mounted helps to overcome nextjs hydration mismatch
  // errors where the state of the user account on the server pass is different
  // than the state on the client side rehydration.
  const environmentAddress = isE2E_Next ? accountQuery.address : userAddress

  return {
    ...queryWithoutAddress,
    isLoading: !isMounted || accountQuery.isConnecting,
    isConnecting: !isMounted || accountQuery.isConnecting,
    connector: isMounted && !isScreenedOut ? accountQuery.connector : undefined,
    // A screened-out wallet reads as disconnected here even if a connection survives.
    userAddress: isScreenedOut ? undefined : environmentAddress,
    // Require both AppKit and wagmi to report connected. AppKit can flip its
    // `isConnected` true before the wagmi connector has called connect() on its
    // provider; gating on both closes the desync window that would otherwise
    // surface as "Please call connect() before request()" from viem.
    isConnected:
      !isScreenedOut &&
      (isE2E_Next
        ? accountQuery.isConnected
        : isConnected && isMounted && accountQuery.isConnected),
    chainId,
    switchNetwork,
    ensName,
    chain,
    screeningBlocked,
    screeningBlockedCount
  }
}

export function UserAccountProvider({
  children,
  screenAddressEndpoint = SCREEN_ADDRESS_ROUTE,
  disableScreening = false
}: PropsWithChildren<{
  screenAddressEndpoint?: string
  /**
   * Opts out of connect-time screening. **Only** for environments that mock the
   * wallet and own no screening route (Storybook); an app must never set it —
   * screening is a compliance control, and the E2E carve-out is handled here via
   * `isE2E_Next`.
   */
  disableScreening?: boolean
}>) {
  const hook = useUserAccountHook(screenAddressEndpoint, disableScreening)
  return <UserAccountContext.Provider value={hook}>{children}</UserAccountContext.Provider>
}

export const useUserAccount = (): UseUserAccountResponse =>
  useMandatoryContext(UserAccountContext, 'UserAccount')
